import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { saveAs } from "file-saver";
import {
  ArrowRightLeftIcon,
  BellRingIcon,
  CirclePlusIcon,
  CopyIcon,
  DownloadIcon,
  EllipsisVerticalIcon,
  FileStackIcon,
  FileTextIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  ReplaceAllIcon,
  ScanTextIcon,
  SignalIcon,
  SignalMediumIcon,
  SparklesIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react";

import { EditUserProfileDialog } from "./EditUserProfileDialog";
import { MoveCandidacyDialog } from "./MoveCandidacyDialog";
import { ParseFromResumeTextDialog } from "./ParseFromResumeTextDialog";
import { UpdatePhoneNumberDialog } from "./UpdatePhoneNumberDialog";
import { ConfirmationDialog } from "@/components";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useNudgeViaSally } from "@/hooks/createAdds/useNudgeViaSally";
import { useDeleteCandidacy } from "@/hooks/mutations/useDeleteCandidacy";
import { useRateCandidacy } from "@/hooks/mutations/useRateCandidacy";
import { useTextAIOutreachHandoff } from "@/hooks/mutations/useTextAIOutreachHandoff";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useGetCompanyFile } from "@/hooks/queries/useGetCompanyFile";
import { useGetCompanyInformation } from "@/hooks/queries/useGetCompanyInformation";
import { useRemoveCandidacyFromAllLists } from "@/hooks/sets/useRemoveCandidacyFromAllLists";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import {
  CandidacyStatus,
  EducationType,
  HttpError,
  ResumeParserAction,
  TextAIOutreachStatus,
  TextAIOutreachWithProxy,
  WorkExperienceType,
} from "@/types";

const MENU_ITEMS_CLASSNAME =
  "p-1.5 [&_[role=menuitem]]:gap-2.5 [&_[role=menuitem]]:px-3 [&_[role=menuitem]]:py-2 [&_[role=menuitem]]:text-[13px] [&_[role=menuitem]]:font-normal";
const MENU_ICON_CLASSNAME = "h-4 w-4 shrink-0 text-slate-400";

export const CandidateSheetMoreActionsMenu = ({
  onDeleted,
}: {
  onDeleted: () => void;
}) => {
  /* Context */
  const { candidacyId, companyName, job } = useSheetScope();
  const { workerId } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const { data: candidacyInfo } = useGetCandidacyInfo({ candidacyId });
  const { data: sheetData } = useGetCandidacySheetData({
    jobId: job.id,
    userId: workerId,
  });
  const logoCompanyFileId =
    useGetCompanyInformation().data?.companyInformation?.companyInformation
      .logoCompanyFileId;
  const { data: companyFile } = useGetCompanyFile({
    companyFileId: logoCompanyFileId ?? 0,
    enabled: !!logoCompanyFileId,
  });
  // Update
  const { mutate: rateCandidacy, isPending: isRatingCandidacy } =
    useRateCandidacy();
  const { mutate: generateExecutiveSummary } =
    novaClient.internal.user.active.generateExecutiveSummary.useMutation();
  const { mutate: handoffTextAIOutreach, isPending: isHandoffPending } =
    useTextAIOutreachHandoff();
  const { mutate: nudgeViaSally, isPending: isNudgeViaSallyPending } =
    useNudgeViaSally();
  // Delete
  const { mutate: deleteCandidacy } = useDeleteCandidacy();

  /* Cache Updates */
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();
  const removeCandidacyFromAllLists = useRemoveCandidacyFromAllLists();

  /* React Hooks */
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [textAIOutreachForHandoff, setTextAIOutreachForHandoff] =
    useState<TextAIOutreachWithProxy | null>(null);
  const [isParseProfileDialogOpen, setIsParseProfileDialogOpen] =
    useState(false);
  const [resumeParserAction, setResumeParserAction] =
    useState<ResumeParserAction>(ResumeParserAction.PARSE_AND_RECONCILE);
  const [isUserProfileDialogOpen, setIsUserProfileDialogOpen] = useState(false);
  const [isUpdatePhoneNumberDialogOpen, setIsUpdatePhoneNumberDialogOpen] =
    useState(false);
  const [isMoveCandidacyDialogOpen, setIsMoveCandidacyDialogOpen] =
    useState(false);
  const [isNudgeViaSallyOpen, setIsNudgeViaSallyOpen] = useState(false);

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  /* Helpers */
  const textAIOutreachHandoffDisabledReason = (() => {
    if (!candidacyInfo?.textAIOutreachWithProxy) return null;
    if (candidacyInfo.textAIOutreachWithProxy.proxy !== null)
      return "Outreach is managed by another job";
    if (candidacyInfo.status === CandidacyStatus.REJECTED)
      return "Not available for rejected candidacies";
    if (
      candidacyInfo.textAIOutreachWithProxy.status ===
      TextAIOutreachStatus.HANDOFF
    )
      return null;

    const allowedStatuses: TextAIOutreachStatus[] = [
      TextAIOutreachStatus.INITIATED,
      TextAIOutreachStatus.ACTIVE,
      TextAIOutreachStatus.CALL_SCHEDULED,
      TextAIOutreachStatus.CALL_RESCHEDULED,
      TextAIOutreachStatus.CALL_CANCELLED,
    ];
    if (!allowedStatuses.includes(candidacyInfo.textAIOutreachWithProxy.status))
      return "Not available for current outreach status";

    return null;
  })();

  // Sally's nudge is once a day at most. Inside that window the item stays
  // visible but says when it went out.
  const SALLY_NUDGE_COOLDOWN_MS = 24 * 60 * 60 * 1000;
  const sallyNudgedAt = candidacyInfo?.sallyNudgedAt
    ? new Date(candidacyInfo.sallyNudgedAt)
    : null;
  const sallyNudgeDisabledReason =
    sallyNudgedAt &&
    Date.now() - sallyNudgedAt.getTime() < SALLY_NUDGE_COOLDOWN_MS
      ? `Sally nudged them ${formatDistanceToNow(sallyNudgedAt, { addSuffix: true })}. Try again after 24h.`
      : null;

  const isTextAIOutreachHandoffDisabled =
    textAIOutreachHandoffDisabledReason !== null ||
    candidacyInfo?.textAIOutreachWithProxy?.status ===
      TextAIOutreachStatus.HANDOFF;

  const generateExecutiveSummaryName = ({
    includeResume,
  }: {
    includeResume: boolean;
  }) => {
    const firstName = sheetData?.profile.firstName ?? "";
    const lastName = sheetData?.profile.lastName ?? "";
    if (firstName === "" && lastName === "") return "Candidate Highlights.pdf";
    return includeResume
      ? `${firstName} ${lastName} - Candidate Highlights and Resume.pdf`
      : `${firstName} ${lastName} - Candidate Highlights.pdf`;
  };

  /* Handlers */
  const handleNudgeViaSally = () => {
    mixpanelUtils.trackButtonClick(
      "Nudge via Sally Confirm",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    const firstName = sheetData?.profile.firstName ?? "the candidate";
    nudgeViaSally(
      { candidacyId, talentId: workerId },
      {
        onSuccess: () => {
          successToast({
            description: `Sally texted ${firstName} that you're trying to reach them.`,
          });
          setIsNudgeViaSallyOpen(false);
        },
        // The 409 body carries the reason (still talking, nudged recently,
        // opted out). Axios's own message is just the status code.
        onError: (error) => {
          errorToast({
            description:
              (error as HttpError<{ message?: string }>).response?.data
                .message ?? error.message,
          });
        },
      },
    );
  };

  const handleDeleteCandidacy = () => {
    mixpanelUtils.trackButtonClick(
      "Delete Candidacy Confirm",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );

    deleteCandidacy(
      { candidacyId },
      {
        onSuccess: (deletedCandidacy) => {
          removeCandidacyFromAllLists({
            candidacyId: deletedCandidacy.id,
          });
          setIsDeleteConfirmOpen(false);
          onDeleted();
        },
      },
    );
  };

  const handleRateCandidacy = () => {
    mixpanelUtils.trackButtonClick(
      "Agentic Candidate Rank",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );

    rateCandidacy(
      { candidacyId },
      {
        onSuccess: (candidacyAgenticScoreChange) => {
          setCandidacyInAllLists({
            candidacyId,
            newAgenticScore: {
              createdAt: candidacyAgenticScoreChange.createdAt,
              fromScore: candidacyAgenticScoreChange.fromScore,
              justification: candidacyAgenticScoreChange.justification,
              toScore: candidacyAgenticScoreChange.toScore,
              userId: candidacyAgenticScoreChange.userId,
            },
          });
          setCandidacyInfo({
            candidacyId,
            agenticScore: candidacyAgenticScoreChange.toScore,
            agenticScoreByUserId: candidacyAgenticScoreChange.userId,
            agenticScoreJustification:
              candidacyAgenticScoreChange.justification,
          });
          successToast({ description: "Candidate successfully ranked!" });
        },
      },
    );
  };

  const handleCopyProfile = ({ isMinimal }: { isMinimal: boolean }) => {
    if (!sheetData) return;

    mixpanelUtils.trackButtonClick(
      isMinimal ? "Copy Profile Minimal" : "Copy Profile Full",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );

    const workExperience = sheetData.workExperience as WorkExperienceType[];
    const text = misc.buildClipboardTextForCandidateProfile({
      education: sheetData.education as EducationType[],
      email: sheetData.email || "",
      firstName: sheetData.profile.firstName,
      lastName: sheetData.profile.lastName,
      location: sheetData.profile.location,
      phone: sheetData.profile.phone,
      professionalSummary: isMinimal ? null : sheetData.professionalSummary,
      workExperience: isMinimal
        ? workExperience.map((we) => ({
            ...we,
            descriptions: [],
          }))
        : workExperience,
    });
    void navigator.clipboard.writeText(text);
    successToast({
      description: isMinimal
        ? "Copied minimal profile to clipboard!"
        : "Copied profile to clipboard!",
    });
  };

  const handleDownloadExecSummary = ({
    includeResume,
  }: {
    includeResume: boolean;
  }) => {
    if (!candidacyInfo) return;

    mixpanelUtils.trackButtonClick(
      includeResume
        ? "Download Exec Summary and Resume"
        : "Download Exec Summary",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );

    // Newly created exec packets live in gcloud storage.
    if (includeResume && candidacyInfo.execSummaryPacketUrl) {
      void fetch(candidacyInfo.execSummaryPacketUrl)
        .then(async (res) => {
          if (!res.ok) throw new Error("Failed to download packet.");
          const blob = await res.blob();
          saveAs(blob, generateExecutiveSummaryName({ includeResume }));
        })
        .catch(() => {
          errorToast({ description: "Failed to download packet." });
        });
      return;
    }

    const onSuccess = (base64String: string) => {
      const blob = misc.base64StringToBytes(base64String);
      saveAs(
        new Blob([blob], { type: "application/pdf" }),
        generateExecutiveSummaryName({ includeResume }),
      );
    };

    generateExecutiveSummary(
      {
        executiveSummary: candidacyInfo.summary ?? "No summary available",
        includeResume,
        companyName,
        jobTitle: job.title,
        signedLogoUrl: companyFile?.signedUrl,
        userId: workerId,
      },
      { onSuccess },
    );
  };

  /* Render */
  const renderMenu = () => {
    if (!candidacyInfo) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* Hidden until the sheet header (group/sheet-header) is hovered. */}
          <Button
            className="h-7 w-7 shrink-0 rounded-md opacity-0 transition-opacity hover:bg-slate-100 group-hover/sheet-header:opacity-100 data-[state=open]:bg-slate-100 data-[state=open]:opacity-100"
            size="icon"
            variant="ghost"
          >
            <EllipsisVerticalIcon className="h-4 w-4 text-slate-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className={cn("w-60", MENU_ITEMS_CLASSNAME)}
        >
          <DropdownMenuItem
            onClick={() => {
              setIsUserProfileDialogOpen(true);
            }}
          >
            <PencilIcon className={MENU_ICON_CLASSNAME} strokeWidth={1.5} />
            Edit candidate name
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              setIsUpdatePhoneNumberDialogOpen(true);
            }}
          >
            <PhoneIcon className={MENU_ICON_CLASSNAME} strokeWidth={1.5} />
            Update phone number
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              setIsUserProfileDialogOpen(true);
            }}
          >
            <CirclePlusIcon className={MENU_ICON_CLASSNAME} strokeWidth={1.5} />
            {sheetData?.permanentStatus
              ? "Edit permanent status"
              : "Add permanent status"}
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ScanTextIcon className={MENU_ICON_CLASSNAME} strokeWidth={1.5} />
              Parse from text
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className={MENU_ITEMS_CLASSNAME}>
              <DropdownMenuItem
                onClick={() => {
                  setResumeParserAction(ResumeParserAction.PARSE_AND_RECONCILE);
                  setIsParseProfileDialogOpen(true);
                }}
              >
                <PlusIcon className={MENU_ICON_CLASSNAME} strokeWidth={1.5} />
                Add to profile
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setResumeParserAction(ResumeParserAction.OVERWRITE);
                  setIsParseProfileDialogOpen(true);
                }}
              >
                <ReplaceAllIcon
                  className={MENU_ICON_CLASSNAME}
                  strokeWidth={1.5}
                />
                Overwrite profile
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <CopyIcon className={MENU_ICON_CLASSNAME} strokeWidth={1.5} />
              Copy profile as text
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className={MENU_ITEMS_CLASSNAME}>
              <DropdownMenuItem
                onClick={() => {
                  handleCopyProfile({ isMinimal: false });
                }}
              >
                <SignalIcon className={MENU_ICON_CLASSNAME} strokeWidth={1.5} />
                Full
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  handleCopyProfile({ isMinimal: true });
                }}
              >
                <SignalMediumIcon
                  className={MENU_ICON_CLASSNAME}
                  strokeWidth={1.5}
                />
                Minimal
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className={cn(!candidacyInfo.summary && "opacity-50")}
              disabled={!candidacyInfo.summary}
            >
              <DownloadIcon className={MENU_ICON_CLASSNAME} strokeWidth={1.5} />
              Download Exec Summary
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className={MENU_ITEMS_CLASSNAME}>
              <DropdownMenuItem
                onSelect={() => {
                  handleDownloadExecSummary({ includeResume: false });
                }}
              >
                <FileTextIcon
                  className={MENU_ICON_CLASSNAME}
                  strokeWidth={1.5}
                />
                Download Exec Summary
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  handleDownloadExecSummary({ includeResume: true });
                }}
              >
                <FileStackIcon
                  className={MENU_ICON_CLASSNAME}
                  strokeWidth={1.5}
                />
                Download Exec Summary and Resume
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          {candidacyInfo.textAIOutreachWithProxy && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <DropdownMenuItem
                      disabled={isTextAIOutreachHandoffDisabled}
                      onClick={() => {
                        setTextAIOutreachForHandoff(
                          candidacyInfo.textAIOutreachWithProxy,
                        );
                      }}
                    >
                      <TriangleAlertIcon
                        className={MENU_ICON_CLASSNAME}
                        strokeWidth={1.5}
                      />
                      {candidacyInfo.textAIOutreachWithProxy.status ===
                      TextAIOutreachStatus.HANDOFF
                        ? "Stevie Outreach Stopped"
                        : "Stop Stevie Outreach"}
                    </DropdownMenuItem>
                  </div>
                </TooltipTrigger>
                {textAIOutreachHandoffDisabledReason && (
                  <TooltipContent side="left">
                    {textAIOutreachHandoffDisabledReason}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
          {candidacyInfo.sallyNudgeEligible && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <DropdownMenuItem
                      disabled={sallyNudgeDisabledReason !== null}
                      onClick={() => {
                        setIsNudgeViaSallyOpen(true);
                      }}
                    >
                      <BellRingIcon
                        className={MENU_ICON_CLASSNAME}
                        strokeWidth={1.5}
                      />
                      Nudge via Sally
                    </DropdownMenuItem>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {sallyNudgeDisabledReason ??
                    "Sally texts them that you're trying to reach them."}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <DropdownMenuItem
            onClick={() => {
              setIsMoveCandidacyDialogOpen(true);
            }}
          >
            <ArrowRightLeftIcon
              className={MENU_ICON_CLASSNAME}
              strokeWidth={1.5}
            />
            Move candidacy
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={isRatingCandidacy}
            onClick={handleRateCandidacy}
          >
            <SparklesIcon className={MENU_ICON_CLASSNAME} strokeWidth={1.5} />
            Agentic Candidate Rank
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-red-600 focus:text-red-600"
            onClick={() => {
              setIsDeleteConfirmOpen(true);
            }}
          >
            <Trash2Icon
              className="h-4 w-4 shrink-0 text-red-500"
              strokeWidth={1.5}
            />
            Delete candidacy
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderDialogs = () => {
    if (!candidacyInfo) return null;

    return (
      <>
        <ConfirmationDialog
          confirmationButtonVariant="destructive"
          confirmButtonText="Delete"
          description="Are you sure you want to delete this candidacy? This action cannot be undone."
          onConfirm={handleDeleteCandidacy}
          onOpenChange={setIsDeleteConfirmOpen}
          open={isDeleteConfirmOpen}
          title="Delete Candidacy"
        />

        <ConfirmationDialog
          confirmButtonText="Stop"
          confirmationButtonVariant="destructive"
          description="This ends Stevie's texting and calling about this job: the conversation is handed off, any scheduled call is cancelled, and the candidate is told to continue with the team. To cancel only the call and keep Stevie active, use Cancel call in the AI Calls section instead."
          isLoading={isHandoffPending}
          onConfirm={() => {
            if (textAIOutreachForHandoff) {
              mixpanelUtils.trackButtonClick(
                "Override Stevie Outreach Confirm",
                MIXPANEL_SCREENS.CANDIDATE_SHEET,
              );
              handoffTextAIOutreach(textAIOutreachForHandoff.id, {
                onSuccess: () => {
                  successToast({ description: "Request submitted" });
                  setTextAIOutreachForHandoff(null);
                },
              });
            }
          }}
          onOpenChange={(open) => {
            if (!open) {
              setTextAIOutreachForHandoff(null);
            }
          }}
          open={!!textAIOutreachForHandoff}
          title="Stop Stevie Outreach"
        />

        <ParseFromResumeTextDialog
          mode={resumeParserAction}
          onOpenChange={setIsParseProfileDialogOpen}
          open={isParseProfileDialogOpen}
          userId={workerId}
        />

        <EditUserProfileDialog
          key={candidacyId}
          onOpenChange={setIsUserProfileDialogOpen}
          onSuccess={() => {
            setIsUserProfileDialogOpen(false);
          }}
          open={isUserProfileDialogOpen}
          userId={workerId}
        />

        <UpdatePhoneNumberDialog
          currentPhoneNumber={sheetData?.profile.phone || ""}
          key={`phone-${candidacyId}`}
          onOpenChange={setIsUpdatePhoneNumberDialogOpen}
          open={isUpdatePhoneNumberDialogOpen}
          userId={workerId}
        />

        <ConfirmationDialog
          confirmButtonText="Send text"
          description="Sally will text the candidate from her number saying you have been trying to reach them about this role, with your number to look out for. She will not pick the conversation back up: any reply lands in Quo for you. Once a day at most."
          isLoading={isNudgeViaSallyPending}
          onConfirm={handleNudgeViaSally}
          onOpenChange={(open) => {
            // Stay open while the text is in flight.
            if (!open && !isNudgeViaSallyPending) setIsNudgeViaSallyOpen(false);
          }}
          open={isNudgeViaSallyOpen}
          title="Nudge via Sally"
        />

        <MoveCandidacyDialog
          agentCurrentlyActive={candidacyInfo.stafferAgentActive}
          candidacyId={candidacyId}
          currentJobId={job.id}
          currentStatus={candidacyInfo.status}
          hasSignedUp={!!sheetData && !sheetData.isUnregistered}
          key={`move-${candidacyId}`}
          onOpenChange={setIsMoveCandidacyDialogOpen}
          onSuccess={() => {
            setIsMoveCandidacyDialogOpen(false);
          }}
          open={isMoveCandidacyDialogOpen}
        />
      </>
    );
  };

  return (
    <>
      {renderMenu()}
      {renderDialogs()}
    </>
  );
};
